import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { transactionService } from '../../services/transactionService';
import { workOrderService } from '../../services/workOrderService';
import { customerService } from '../../services/customerService';
import { inventoryService } from '../../services/inventoryService';
import { FiSave, FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import { settingService } from '../../services/settingService';

const TransactionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  // ============================================
  // STATE
  // ============================================
  const [formData, setFormData] = useState({
    id: '',
    workOrderId: '',
    customerId: '',
    date: new Date().toISOString().split('T')[0],
    items: [],
    paymentMethod: 'cash',
    paymentStatus: 'pending',
    notes: ''
  });

  const [diskonPersen, setDiskonPersen] = useState(0);
  const [markupPersen, setMarkupPersen] = useState(0);
  const [taxSettings, setTaxSettings] = useState({ rate: 11, name: 'PPN' });

  const [workOrders, setWorkOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState({ inventoryId: '', quantity: 1, price: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const paymentMethods = [
    { value: 'cash', label: 'Tunai' },
    { value: 'transfer', label: 'Transfer Bank' },
    { value: 'qris', label: 'QRIS' },
    { value: 'credit', label: 'Kredit' }
  ];

  const paymentStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  // ============================================
  // LOAD DATA
  // ============================================
  useEffect(() => {
    const init = async () => {
      await loadData();
      if (isEdit) {
        await loadTransaction();
      }
    };
    init();
  }, [id]);

  useEffect(() => {
    const loadTaxSettings = async () => {
      try {
        const rate = await settingService.getTaxRate();
        const name = await settingService.getTaxName();
        setTaxSettings({ rate: rate || 11, name: name || 'PPN' });
      } catch (error) {
        console.error('Error loading tax settings:', error);
      }
    };
    loadTaxSettings();
  }, []);

  const loadData = async () => {
    try {
      const [woData, customerData, inventoryData] = await Promise.all([
        workOrderService.getAll(),
        customerService.getAll(),
        inventoryService.getAll()
      ]);
      setWorkOrders(woData || []);
      setCustomers(customerData || []);
      setInventoryItems(inventoryData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Gagal memuat data');
    }
  };

  const loadTransaction = async () => {
    try {
      const transaction = await transactionService.getById(id);
      if (transaction) {
        // 🔥 PASTIKAN ITEMS PUNYA FIELD total
        const items = (transaction.items || []).map(item => ({
          ...item,
          total: (item.quantity || 0) * (item.price || 0)
        }));

        setFormData({
          ...transaction,
          items: items,
          date: transaction.date ? transaction.date.split('T')[0] : new Date().toISOString().split('T')[0]
        });
        
        // 🔥 LOAD DISKON & MARKUP
        setDiskonPersen(transaction.discountPercent || 0);
        setMarkupPersen(transaction.markupPercent || 0);
      } else {
        setError('Transaction not found');
      }
    } catch (error) {
      console.error('Error loading transaction:', error);
      setError('Gagal memuat transaksi');
    }
  };

  // ============================================
  // HANDLE FUNCTIONS
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    setFormData(prev => ({
      ...prev,
      customerId,
      workOrderId: ''
    }));
  };

  const handleAddItem = () => {
    if (!selectedItem.inventoryId || selectedItem.quantity <= 0) {
      setError('Pilih item dan masukkan quantity');
      return;
    }

    const inventory = inventoryItems.find(i => i.id === selectedItem.inventoryId);
    if (!inventory) {
      setError('Item tidak ditemukan');
      return;
    }

    if (inventory.stock < selectedItem.quantity) {
      setError(`Stock tidak cukup. Tersedia: ${inventory.stock} ${inventory.unit}`);
      return;
    }

    const price = selectedItem.price || inventory.sellPrice || 0;
    const quantity = selectedItem.quantity || 1;

    const newItem = {
      inventoryId: inventory.id,
      name: inventory.name,
      quantity: quantity,
      unit: inventory.unit || 'pcs',
      price: price,
      total: quantity * price
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setSelectedItem({ inventoryId: '', quantity: 1, price: 0 });
    setError('');
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    if (name === 'inventoryId') {
      const inventory = inventoryItems.find(i => i.id === value);
      setSelectedItem(prev => ({
        ...prev,
        inventoryId: value,
        price: inventory ? inventory.sellPrice : 0
      }));
    } else {
      setSelectedItem(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    }
  };

  // ============================================
  // PERHITUNGAN
  // ============================================
  const calculateTotals = () => {
    // 🔥 PASTIKAN items ADA DAN PUNYA total
    let subtotal = 0;
    if (formData.items && formData.items.length > 0) {
      subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    }
    
    const diskon = (subtotal * diskonPersen) / 100;
    const afterDiscount = subtotal - diskon;
    const markup = (afterDiscount * markupPersen) / 100;
    const beforeTax = afterDiscount + markup;
    const taxRate = taxSettings.rate || 11;
    const tax = (beforeTax * taxRate) / 100;
    const total = beforeTax + tax;

    return {
      subtotal: Math.round(subtotal),
      diskon: Math.round(diskon),
      afterDiscount: Math.round(afterDiscount),
      markup: Math.round(markup),
      beforeTax: Math.round(beforeTax),
      tax: Math.round(tax),
      taxRate: Math.round(taxRate),
      taxName: taxSettings.name || 'PPN',
      total: Math.round(total)
    };
  };

  // ============================================
  // SUBMIT
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.items.length === 0) {
      setError('Tambah minimal 1 item');
      return;
    }

    setLoading(true);
    try {
      const totals = calculateTotals();
      const { subtotal, diskon, markup, tax, taxRate, taxName, total } = totals;

      // 🔥 BUAT OBJECT DENGAN SEMUA FIELD YANG DIPERLUKAN
      const dataToSave = {
        workOrderId: formData.workOrderId || null,
        customerId: formData.customerId,
        date: formData.date,
        items: formData.items.map(item => ({
          ...item,
          total: (item.quantity || 0) * (item.price || 0)
        })),
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentStatus,
        notes: formData.notes || '',
        subtotal: Math.round(subtotal),
        discount: Math.round(diskon),
        discountPercent: Math.round(diskonPersen),
        markup: Math.round(markup),
        markupPercent: Math.round(markupPersen),
        tax: Math.round(tax),
        taxPercent: Math.round(taxRate),
        taxName: taxName || 'PPN',
        total: Math.round(total),
        paymentDate: formData.paymentStatus === 'paid' ? new Date().toISOString() : null
      };

      console.log('📤 [SUBMIT] Data to save:', JSON.stringify(dataToSave, null, 2));

      let result;
      if (isEdit) {
        result = await transactionService.update(id, dataToSave);
      } else {
        result = await transactionService.create(dataToSave);
      }

      console.log('✅ [SUBMIT] Result:', result);

      if (result) {
        navigate('/transactions');
      } else {
        setError('Gagal menyimpan transaksi');
      }
    } catch (error) {
      console.error('❌ Error saving transaction:', error);
      const errorMessage = error?.message || error?.toString() || 'Terjadi kesalahan saat menyimpan';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableWorkOrders = () => {
    const customerId = formData.customerId;
    if (!customerId) return [];
    return workOrders.filter(wo => 
      wo.customerId === customerId && 
      (wo.status === 'completed' || wo.status === 'delivered')
    );
  };

  const getWorkOrderLabel = (wo) => {
    return `${wo.woNumber} - ${wo.projectName}`;
  };

  // ============================================
  // RENDER
  // ============================================
  const totals = calculateTotals();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">
          {isEdit ? 'Edit Transaksi' : 'Transaksi Baru'}
        </h1>
        <button
          onClick={() => navigate('/transactions')}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <FiX size={20} />
          <span>Batal</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          ❌ {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Transaction Info */}
        <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
            Informasi Transaksi
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Customer *
              </label>
              <select
                name="customerId"
                value={formData.customerId}
                onChange={handleCustomerChange}
                required
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Pilih Customer</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Work Order
              </label>
              <select
                name="workOrderId"
                value={formData.workOrderId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Pilih Work Order</option>
                {getAvailableWorkOrders().map(wo => (
                  <option key={wo.id} value={wo.id}>{getWorkOrderLabel(wo)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Tanggal Transaksi *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Metode Pembayaran *
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {paymentMethods.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Status Pembayaran *
              </label>
              <select
                name="paymentStatus"
                value={formData.paymentStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {paymentStatuses.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Diskon & Markup */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Diskon (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={diskonPersen}
                onChange={(e) => setDiskonPersen(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Biaya Penyesuaian (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={markupPersen}
                onChange={(e) => setMarkupPersen(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Catatan
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="2"
                placeholder="Catatan transaksi..."
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
            Item Transaksi
          </h3>

          {/* Add Item */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Item
              </label>
              <select
                name="inventoryId"
                value={selectedItem.inventoryId}
                onChange={handleItemChange}
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Pilih Item</option>
                {inventoryItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name} (Stok: {item.stock} {item.unit})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={selectedItem.quantity}
                onChange={handleItemChange}
                min="1"
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                Harga
              </label>
              <input
                type="number"
                name="price"
                value={selectedItem.price}
                onChange={handleItemChange}
                min="0"
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-4">
              <button
                type="button"
                onClick={handleAddItem}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <FiPlus size={20} />
                <span>Tambah Item</span>
              </button>
            </div>
          </div>

          {/* Items List */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--bg-secondary)]">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Item</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Qty</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Unit</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Harga</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Total</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-[var(--text-secondary)]">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {formData.items.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-4 text-center text-[var(--text-secondary)]">
                      Belum ada item
                    </td>
                  </tr>
                ) : (
                  formData.items.map((item, index) => (
                    <tr key={index} className="border-t border-[var(--border-color)]">
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{item.name}</td>
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{item.quantity}</td>
                      <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">{item.unit}</td>
                      <td className="px-4 py-2 text-sm text-[var(--text-primary)]">
                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(item.price)}
                      </td>
                      <td className="px-4 py-2 text-sm font-semibold text-[var(--text-primary)]">
                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(item.total)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          {formData.items.length > 0 && (
            <div className="mt-4 p-4 bg-[var(--bg-secondary)] rounded-lg">
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-[var(--text-secondary)]">Subtotal</span>
                  <span className="text-[var(--text-primary)]">
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(totals.subtotal)}
                  </span>
                </div>
                {totals.diskon > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Diskon {diskonPersen}%</span>
                    <span>- {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(totals.diskon)}</span>
                  </div>
                )}
                {totals.markup > 0 && (
                  <div className="flex justify-between text-sm text-blue-600">
                    <span>Biaya Penyesuaian {markupPersen}%</span>
                    <span>+ {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(totals.markup)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-purple-600">
                  <span>{totals.taxName} {totals.taxRate}%</span>
                  <span>+ {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(totals.tax)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-[var(--border-color)]">
                  <span className="text-[var(--text-primary)]">Total</span>
                  <span className="text-[var(--text-primary)]">
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(totals.total)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex items-center space-x-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <FiSave size={20} />
            <span>{loading ? 'Menyimpan...' : isEdit ? 'Update' : 'Simpan'}</span>
          </button>

          <button
            type="button"
            onClick={() => navigate('/transactions')}
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  );
};

export default TransactionForm;